import { FC, useCallback } from 'react';
import { CardContent } from '@mui/material';
import { Hooks } from '@wellstone-solutions/common';
import { TextField } from '@wellstone-solutions/web';
import { AxiosResponse } from 'axios';
import { Trans, useTranslation } from 'react-i18next';

import { CardHeader } from '@components/ui/Card';
import { ResetPasswordForm } from '@modules/auth/components/ResetPasswordForm';
import { FORGOT_PASSWORD_FORM_ID } from '@modules/auth/constants';
import { ForgotPasswordSchema } from '@modules/auth/schemas/forgot-password-schema';
import { useAuthStore } from '@modules/auth/store/auth-store';

import type { ForgotPasswordMutationResult } from './types';
import type { ForgotPasswordSchemaType } from '@modules/auth/schemas/forgot-password-schema';
import type {
  PasswordResetRequest,
  PasswordResetRequestInputRequest,
} from '@wellstone-solutions/api/v2';

type ForgotPasswordFormType = ReturnType<
  typeof Hooks.useForm<ForgotPasswordSchemaType>
> & {
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
};

interface ForgotPasswordInitialProps {
  mutation: ForgotPasswordMutationResult;
  forceSuccess: React.Dispatch<React.SetStateAction<boolean>>;
}

const ForgotPasswordInitial: FC<ForgotPasswordInitialProps> = ({
  mutation,
  forceSuccess,
}) => {
  const { t } = useTranslation();
  const { setUserInfo } = useAuthStore();
  const handleSuccess = useCallback(
    (
      _response: AxiosResponse<PasswordResetRequest>,
      variables: PasswordResetRequestInputRequest
    ) => {
      setUserInfo({ email: variables.email });
    },
    [setUserInfo]
  );

  return (
    <>
      <CardHeader
        title={t('auth.forgot_password.title')}
        subheaders={[
          <Trans
            key="auth.forgot_password.subheader"
            i18nKey="auth.forgot_password.subheader"></Trans>,
        ]}
      />
      <CardContent sx={styles.cardContent}>
        <ResetPasswordForm
          formId={FORGOT_PASSWORD_FORM_ID}
          schema={ForgotPasswordSchema}
          initialValues={{ email: '' }}
          mutation={mutation}
          mutateOptions={{
            onSuccess: handleSuccess,
            onError: () => {
              forceSuccess(true);
            },
          }}>
          {(form) => {
            const formik = form as ForgotPasswordFormType;
            return (
              <TextField
                fullWidth
                formField="email"
                label={t('auth.email')}
                type="email"
                id="email"
                inputProps={{ maxLength: 128 }}
                onChange={formik.handleFieldChange('email')}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                error={formik.touched.email && Boolean(formik.errors.email)}
              />
            );
          }}
        </ResetPasswordForm>
      </CardContent>
    </>
  );
};

const styles = {
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '1.5rem',
  },
};

export { ForgotPasswordInitial };
